(function () {
  "use strict";
  /**
   * Secuvy Privacy Widget
   * =====================================================
   * Version: 1.0.0
   * Description: Client-side privacy widget for Secuvy Privacy consent integration and DSAR handling.
   * Author: Secuvy Inc.
   *
   * Keep this file as a single script that clients can include via a <script> tag.
   */

  // =====================================================
  // 🔹 Module-level configuration
  // =====================================================
  const dsarDomain = "https://cache.zinus.secuvy.com";
  const consentUrl = "https://consent.zinus.secuvy.com/policy-config/prod/policy.js";
  const menuOptions = ["cookies", "subject-request", "company", "legalcenter"];
  const dsarPath = "/privacy/rtype";
  const dsarParams = {};

  // =====================================================
  // 🔹 Global variables for routing
  // =====================================================
  let isAlreadyRender = false;
  let isConsentUpdated = false;
  let routeLocation = null;
  let routeSubmenu = null;

  // =====================================================
  // 🔹 Klaro config object (translations, services, etc.)
  // =====================================================
  let Klaro;
  let KlaroConfig;

  // =====================================================
  // 🔹 Consent Update Handler
  // =====================================================
  const updateSecuvyConsent = () => {
    try {
      const manager = KlaroConfig.getManager();
      const consents = manager?.consents || {};
      const klaroConfig = window.klaro?.defaultConfig || {};
      const necessaryText = klaroConfig.translations?.en?.necessaryText || "";
      const klaroServices = Array.isArray(klaroConfig.services) ? klaroConfig.services : [];

      const result = {
        categoryStatus: {},
        acceptStatus: false,
      };

      // ----------------------------------------------
      // 🔸 Process services and assign consent status
      // ----------------------------------------------
      if (klaroServices.length > 0) {
        klaroServices.forEach(service => {
          const allowed = Boolean(consents[service.name]);

          (service.purposes || []).forEach(purpose => {
            const categoryName = String(purpose);

            if (categoryName === necessaryText) return;

            if (!Object.prototype.hasOwnProperty.call(result.categoryStatus, categoryName)) {
              result.categoryStatus[categoryName] = false;
            }

            // Update status based on service consent
            result.categoryStatus[categoryName] = result.categoryStatus[categoryName] || allowed;
          });
        });
      }

      // ----------------------------------------------
      // 🔸 Determine overall accept status
      // ----------------------------------------------
      result.acceptStatus = Object.values(result.categoryStatus).some(Boolean);

      // ----------------------------------------------
      // 🔸 Emit consent result
      // ----------------------------------------------
      try {
        window.dispatchEvent(new CustomEvent("SecuvyConsentStatus", { detail: result }));
      } catch (err) {
        console.error("[Secuvy] Error dispatching consent event:", err);
      }
    } catch (err) {
      console.error("[Secuvy] Consent update failed:", err);
    }
  };

  // =====================================================
  // 🔹 DOM utilities
  // =====================================================
  /**
   * Wait for an element with the given id to appear in the DOM, then call cb(el).
   * - Immediately checks document.getElementById
   * - Falls back to DOMContentLoaded if body not present yet
   * - Uses MutationObserver with a 5s safety timeout
   *
   * @param {string} selectorOrId Element id to wait for
   * @param {function} cb Callback invoked with element or null
   */
  const waitForElement = (selectorOrId, cb) => {
    try {
      const immediate = document.getElementById(selectorOrId);
      if (immediate) {
        cb(immediate);
        return;
      }
    } catch (err) {
      console.error("[Secuvy] waitForElement error:", err);
    }

    // If body doesn't exist yet, wait for DOMContentLoaded
    if (!document.body) {
      const onDom = () => {
        document.removeEventListener("DOMContentLoaded", onDom);
        waitForElement(selectorOrId, cb);
      };
      document.addEventListener("DOMContentLoaded", onDom);
      return;
    }

    // MutationObserver fallback
    let observed = false;
    const root = document.documentElement || document.body;
    const observer = new MutationObserver((mutations, obs) => {
      try {
        const el = document.getElementById(selectorOrId);
        if (el) {
          obs.disconnect();
          clearTimeout(timer);
          cb(el);
          observed = true;
        }
      } catch (err) {
        console.error("[Secuvy] waitForElement error:", err);
      }
    });
    observer.observe(root, { childList: true, subtree: true });

    // Safety timeout: stop observing after 5s
    const timer = setTimeout(() => {
      if (!observed) {
        observer.disconnect();
        const el = document.getElementById(selectorOrId);
        cb(el || null);
      }
    }, 5000);
  };

  // =====================================================
  // 🔹 Widget rendering
  // =====================================================
  /**
   * Insert the Secuvy widget HTML markup into the page and ensure the
   * policy script (consentUrl) is loaded into HEAD. Keeps logic identical
   * to original implementation but with cleaner error handling.
   */
  const toRenderWeight = () => {
    try {
      // Ensure policy URL script is appended to HEAD (cache-bust by timestamp)
      const cookiesScript = document.createElement("script");
      cookiesScript.src = `${consentUrl}?v=${Date.now()}`;
      cookiesScript.id = "policyScript";
      cookiesScript.async = true;

      // Remove existing policyScript if present
      const existingScript = document.getElementById("policyScript");
      if (existingScript) existingScript.remove();

      document.head.appendChild(cookiesScript);

      // Build shared assets base path
      const e = `${consentUrl.split("/").splice(0, 3).join("/")}/common`;

      // Widget markup (desktop + mobile + drawer). Kept as a single template string
      const t = `<div class=secuvy-mobile-widget-card id=secuvy-mobile-widget-card><div class=secuvy-mobile-widget-card-wrapper><div class=secuvy-mobile-widget-close-btn-wrapper><img alt=secuvy-mobile-widget-close-btn src=${e}/secuvy-mobile-widget-close-btn.svg id=secuvy-mobile-widget-button-closed height=16px onclick=hideSecuvyMobileWidget() width=16px></div><div class=secuvy-widget-card-desc id=secuvy-mobile-popup-description></div><div class=secuvy-mobile-widget-btn-wrapper><button class="secuvy-widget-btn secuvy-widget-btn-outlined"id=secuvy-mobile-weight-reject-all-btn onclick=resetContentClick()>Reject All</button><button class="secuvy-widget-btn secuvy-widget-btn-filled"id=secuvy-mobile-weight-accept-all-btn onclick='saveContentClick("accept_all")'>That's ok</button></div><div class=secuvy-mobile-widget-link-group><a href="#" class=secuvy-widget-link id=secuvy-mobile-widget-link-policies onclick='openSecuvyDsarDrawer("secuvy-appear-drawer","policies")'>View Policies</a><span class=secuvy-widget-link-divider></span><a href="#" class=secuvy-widget-link id=secuvy-mobile-widget-link-manage onclick='openSecuvyDsarDrawer("secuvy-appear-drawer","cookies")'>Manage Preferences</a></div><div class=secuvy-widget-link-dsr><a href="#" class=secuvy-widget-link id=secuvy-mobile-widget-link-dsr onclick='openSecuvyDsarDrawer("secuvy-appear-drawer","subject-request")'>Do not Sell My Personal Information</a></div></div><span class="secuvy-gpc-section-centent secuvy-mobile-gpc-section-centent"id=secuvy-mobile-gpc-section-centent><span class=secuvy-gpc-section-box id=secuvy-mobile-gpc-section-box></span><p class=secuvy-gpc-section-desc id=secuvy-mobile-gpc-section-desc>GPC Signal detected</span></div><div class=secuvy-appear-drawer id=secuvy-side-drawer-modal-id><div class=secuvy-iframe-header id=secuvy-iframe-header><span class=secuvy-iframe-header-close onclick=closeSecuvyDsarDrawer()><img alt=secuvy-iframe-drawer-close-btn src=${e}/secuvy-iframe-drawer-close-btn.svg></span></div><div class=secuvy-iframe-footer><div class=secuvy-iframe-footer-wrapper><span class=footer-text>Powered by</span> <img alt=secuvy-logo src=${e}/secuvy-logo.svg height=16px width=80px></div></div></div><div class=secuvy-widget-button id=secuvy-widget-button onclick=showSecuvyWidget(!1)><img alt=secuvy-widget-button-logo src=${e}/secuvy-widget-button-logo.svg id=secuvy-widget-button-logo class=secuvy-widget-button-images style=display:none> <img alt=secuvy-widget-button-closed src=${e}/secuvy-widget-button-closed.svg id=secuvy-widget-button-closed class=secuvy-widget-button-images style=display:block></div><div class=secuvy-widget-card id=secuvy-widget-card><div class=secuvy-widget-card-body><div class=secuvy-widget-card-desc id=secuvy-popup-description></div><div class=secuvy-widget-btn-group><button class="secuvy-widget-btn secuvy-widget-btn-outlined"id=secuvy-weight-reject-all-btn onclick=resetContentClick()>Reject All</button> <button class="secuvy-widget-btn secuvy-widget-btn-filled"id=secuvy-weight-accept-all-btn onclick='saveContentClick("accept_all")'>That's Ok</button></div><div class=secuvy-widget-link-group><a href="#" class=secuvy-widget-link id=secuvy-widget-link-policies onclick='openSecuvyDsarDrawer("secuvy-appear-drawer","policies")'>View Policies</a> <span class=secuvy-widget-link-divider></span> <a href="#" class=secuvy-widget-link id=secuvy-widget-link-manage onclick='openSecuvyDsarDrawer("secuvy-appear-drawer","cookies")'>Manage Preferences</a></div><a href="#" class=secuvy-widget-link id=secuvy-widget-link-dsr onclick='openSecuvyDsarDrawer("secuvy-appear-drawer","subject-request")'>Do not Sell My Personal Information</a></div><span class=secuvy-gpc-section-centent id=secuvy-gpc-section-centent><span class=secuvy-gpc-section-box id=secuvy-gpc-section-box></span><p class=secuvy-gpc-section-desc id=secuvy-gpc-section-desc>GPC Signal detected</span></div>`;

      // Render the widget when body exists. keep retry to handle early head-run.
      const renderWidget = () => {
        if (!document.body) {
          return setTimeout(renderWidget, 50);
        }

        // Avoid duplicate insertion
        if (!document.getElementById("secuvy-mobile-widget-card")) {
          try {
            document.body.insertAdjacentHTML("beforeend", t);
          } catch (err) {
            console.error("[Secuvy] Error inserting widget HTML:", err);
          }
        }
      };

      renderWidget();
    } catch (err) {
      console.error("[Secuvy] Exception in widget loading:", err);
    }
  };

  // =====================================================
  // 🔹 Mobile widget helpers
  // =====================================================
  /**
   * Hide or show mobile widget card
   * @param {boolean} status true => show, false => hide
   */
  const hideSecuvyMobileWidget = (status = false) => {
    waitForElement("secuvy-mobile-widget-card", el => {
      if (!el) return;
      try {
        el.style.display = status ? "block" : "none";
      } catch (err) {
        console.error("[Secuvy] Error hiding mobile widget:", err);
      }
    });
  };

  /**
   * Toggle the floating widget button and card
   * @param {boolean} status true => open, false => close
   */
  const showSecuvyWidget = status => {
    waitForElement("secuvy-widget-button", btn => {
      const logo = document.getElementById("secuvy-widget-button-logo");
      const closed = document.getElementById("secuvy-widget-button-closed");
      const card = document.getElementById("secuvy-widget-card");

      if (!btn || !logo || !closed || !card) return;

      try {
        if (status) {
          closed.style.display = "block";
          logo.style.display = "none";
          btn.setAttribute("onclick", "showSecuvyWidget(false);");
          if (isAlreadyRender) {
            card.style.display = "flex";
          }
        } else {
          closed.style.display = "none";
          logo.style.display = "block";
          card.style.display = "none";
          btn.setAttribute("onclick", "showSecuvyWidget(true);");
        }
      } catch (err) {
        console.error("[Secuvy] Exception in widget drawer:", err);
      }
    });
  };

  // =====================================================
  // 🔹 DSAR drawer & iframe routing
  // =====================================================
  /**
   * Open the DSAR drawer and create the iframe (if not already created).
   * @param {string} mode drawer mode
   * @param {string|null} menu menu route
   * @param {string|null} submenu submenu route
   */
  const openSecuvyDsarDrawer = (mode, menu, submenu) => {
    routeLocation = menu;
    routeSubmenu = submenu;
    hideSecuvyMobileWidget(false);

    if (isAlreadyRender) {
      toRouteIframeChild(menu, submenu);
      return;
    }

    if (mode) {
      dsarParams.mode = mode;
      if (menu) {
        dsarParams.menu = menu;
        if (menu === "policies") dsarParams.policy = submenu;
      } else {
        dsarParams.menu = null;
      }
    }

    try {
      const sideModal = document.getElementById("secuvy-side-drawer-modal-id");
      if (sideModal && !document.getElementById("secuvy-dsar-frame")) {
        // Create loader DIV (outside iframe)
        const loaderDiv = document.createElement("div");
        loaderDiv.id = "secuvy-main-page-loader";
        loaderDiv.className = "secuvy-main-page-loader-container";

        // Create main loader container
        const mainLoader = document.createElement("div");
        mainLoader.className = "secuvy-main-loader";

        // Create 4 loader items
        for (let i = 0; i < 4; i++) {
          const item = document.createElement("div");
          item.className = "secuvy-main-loader-item";
          mainLoader.appendChild(item);
        }

        // Create loader text
        const loaderText = document.createElement("div");
        loaderText.className = "secuvy-main-loader-text";
        loaderText.textContent = "Loading...";

        // Append children
        loaderDiv.appendChild(mainLoader);
        loaderDiv.appendChild(loaderText);

        // Append to modal
        sideModal.appendChild(loaderDiv);

        // Create iframe
        const dsarFrame = document.createElement("iframe");
        dsarFrame.id = "secuvy-dsar-frame";
        dsarFrame.setAttribute("loading", "lazy");
        dsarFrame.setAttribute("frameborder", "0");
        dsarFrame.setAttribute("name", "Secuvy DSAR Frame");
        dsarFrame.setAttribute("title", "Secuvy Privacy Request");
        dsarFrame.style.width = "100%";
        dsarFrame.style.minHeight = "calc(100% - 52px)";
        dsarFrame.style.height = "auto";
        dsarFrame.style.overflow = "hidden";
        dsarFrame.style.opacity = "0"; // start hidden

        // Load real DSAR content
        dsarFrame.src = `${dsarDomain}${dsarPath}?key=${
          window?.klaroConfig?.consentOrgId
        }&${new URLSearchParams(dsarParams).toString()}`;

        // When iframe finishes loading, fade in and remove loader
        dsarFrame.onload = () => {
          loaderDiv.remove();
          dsarFrame.style.transition = "opacity 0.3s ease";
          requestAnimationFrame(() => (dsarFrame.style.opacity = "1"));
        };

        sideModal.appendChild(dsarFrame);
      }

      showSecuvyWidget(true);
    } catch (err) {
      console.error("[Secuvy] Error opening DSAR drawer:", err);
    }
  };

  /**
   * Close the DSAR drawer and hide the floating button
   */
  const closeSecuvyDsarDrawer = () => {
    try {
      const btn = document.getElementById("secuvy-widget-button");
      if (btn) btn.style.display = "none";
      // call exposed global as original code relied on window.showSecuvyWidget
      if (window.showSecuvyWidget) window.showSecuvyWidget(false);
      const side = document.getElementById("secuvy-side-drawer-modal-id");
      if (side) side.classList.remove("show");
    } catch (err) {
      console.error("[Secuvy] Error closing DSAR drawer:", err);
    }
  };

  /**
   * Send a route message to the iframe (child) with Klaro saved consents
   * @param {string} menu
   * @param {string|null} submenu
   */
  const toRouteIframeChild = (menu, submenu = null) => {
    try {
      if (!KlaroConfig) return;
      const iframe = document.getElementById("secuvy-dsar-frame");
      if (!iframe) return;
      const side = document.getElementById("secuvy-side-drawer-modal-id");
      if (side) side.classList.add("show");

      iframe.contentWindow.postMessage(
        {
          type: "route",
          location: menu,
          submenu,
          cookiesDetails: KlaroConfig.getManager().savedConsents,
        },
        "*",
      );
    } catch (err) {
      console.error("[Secuvy] Error routing to iframe child:", err);
    }
  };

  // =====================================================
  // 🔹 UI close helpers
  // =====================================================
  /**
   * Reset the floating UI to closed state and hide mobile widget
   */
  const onCloseCookieWeight = () => {
    const btn = document.getElementById("secuvy-widget-button");
    if (btn) btn.setAttribute("onclick", "showSecuvyWidget(true);");
    const logo = document.getElementById("secuvy-widget-button-logo");
    const closed = document.getElementById("secuvy-widget-button-closed");
    const card = document.getElementById("secuvy-widget-card");
    if (logo) logo.style.display = "block";
    if (closed) closed.style.display = "none";
    if (card) card.style.display = "none";
    if (btn) btn.style.display = "none";
    hideSecuvyMobileWidget();
  };

  // =====================================================
  // 🔹 Cookie helpers
  // =====================================================
  /**
   * Create a visitor cookie when none exists. Returns existing or new identifier.
   * @return {string|null}
   */
  const createVisitorCookie = () => {
    try {
      const curr = new Date();
      const random = Math.floor(1e16 * Math.random()).toString();
      const existing = getCookieValue("secuvy-consent");
      if (existing) return existing;

      const days = window.klaroConfig?.cookieExpiresAfterDays ?? 1;
      const expires = new Date(curr.setDate(curr.getDate() + days)).toUTCString();

      document.cookie = `secuvy-consent=${random};expires=${expires};path=/`;
      return random;
    } catch (err) {
      console.error("[Secuvy] Error creating visitor cookie:", err);
      return null;
    }
  };

  /**
   * Manually delete cookies for services that are not allowed (retry loop)
   * @param {Array} services Klaro service definitions
   * @param {number} count retry count
   */
  const cookieDeleteManual = (services, count) => {
    try {
      let i = 0;
      const interval = setInterval(() => {
        i += 1;
        if (i > count) {
          clearInterval(interval);
          return;
        }
        services.forEach(svc => {
          if (!svc.externalScript) {
            try {
              if (!window.klaro.getManager().savedConsents[svc.name]) {
                document.cookie = `${svc.name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${svc.cookies[0][2]};path=/`;
              }
            } catch (ex) {
              console.error("[Secuvy] Error deleting cookies:", ex);
              // ignore per-service errors
            }
          }
        });
      }, 1000);
    } catch (err) {
      console.error("[Secuvy] Error deleting cookies:", err);
    }
  };

  // =====================================================
  // 🔹 Iframe / Klaro config relay
  // =====================================================
  /**
   * Send Klaro configuration to the DSAR iframe once loaded
   * @param {boolean} iframeLoaded
   */
  const setKlaroConfig = (iframeLoaded = false) => {
    try {
      const iframe = document.getElementById("secuvy-dsar-frame");
      if (iframe && iframeLoaded) {
        iframe.contentWindow.postMessage(
          {
            type: "klaroConfig",
            details: Klaro,
            cookieSelected: getCookieValue("secuvy"),
          },
          "*",
        );
        isAlreadyRender = true;
      }
    } catch (err) {
      console.error("[Secuvy] Error creating visitor cookie:", err);
    }
  };

  // =====================================================
  // 🔹 Consent save / API reporting
  // =====================================================
  /**
   * Save consents (accept all or specific) and report to backend API.
   * Maintains original timing behavior with setTimeouts to allow Klaro to apply changes.
   * @param {string} status optional status (e.g. "accept_all")
   */
  const saveContentClick = async status => {
    try {
      const getManager = KlaroConfig.getManager();
      if (status === "accept_all") {
        await getManager.changeAll(true);
        if (Klaro && Klaro.consentReload) {
          window.location.reload();
        }
      }

      // Allow Klaro to apply consents then send payload
      setTimeout(async () => {
        await getManager.saveAndApplyConsents();
        setTimeout(() => {
          try {
            const payload = {
              user: "guest",
              version: "1.0.0",
              uniqueVisitorId: createVisitorCookie(),
              countryCode: window.secuvy_config_country,
              services: [],
              policyName: window.secuvy_config_policyDetails
                ? window.secuvy_config_policyDetails.policyName
                : null,
              policyId: window.secuvy_config_policyDetails
                ? window.secuvy_config_policyDetails.policyId
                : null,
              domain: window.location.hostname || window.location.host,
              consentId: window?.klaroConfig?.consentTemplateId,
              cookiePageUrl: window.location.href,
            };

            // If Secuvy policy details exist, try to read the stored visitor id cookie
            if (window.secuvy_config_policyDetails) {
              const found = document.cookie
                .split(";")
                .find(c => c.includes(`${window.secuvy_config_policyDetails}=`));
              if (found) payload.uniqueVisitorId = found.split("=")[1] || payload.uniqueVisitorId;
            }

            // Append service-level consent details if Klaro defaultConfig is available
            if (
              window.klaro &&
              window.klaro.defaultConfig &&
              Array.isArray(window.klaro.defaultConfig.services)
            ) {
              window.klaro.defaultConfig.services.forEach(({ cookies, name, purposes, title }) => {
                payload.services.push({
                  cookies,
                  name,
                  purposes,
                  title,
                  consent: (getManager.savedConsents && getManager.savedConsents[name]) || false,
                });
              });
            }

            const xhr = new XMLHttpRequest();
            let incTrue = false;
            if (payload.services.every(s => s.consent === true)) incTrue = true;
            xhr.open("POST", window.secuvy_config_api_url);
            xhr.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
            xhr.send(JSON.stringify({ ...payload, isCookieOptedIn: incTrue }));
          } catch (errInner) {
            console.error("[Secuvy] Error saving consent:", errInner);
          }
        }, 100);
      }, 1000);

      // update the secuvy consent status
      updateSecuvyConsent();

      // close the cookie weight
      onCloseCookieWeight();
    } catch (err) {
      console.error("[Secuvy] Error saving consent:", err);
    }
  };

  // =====================================================
  // 🔹 Consent toggles
  // =====================================================
  /**
   * Update consent for a list of cookies (array of {key, value}) then save
   * @param {Array<{key:string,value:boolean}>} cookiesName
   */
  const toggleChange = async cookiesName => {
    try {
      for (let i = 0; i < cookiesName.length; i++) {
        await KlaroConfig.getManager().updateConsent(cookiesName[i].key, cookiesName[i].value);
      }
      saveContentClick();
    } catch (err) {
      console.error("[Secuvy] Error toggling consent:", err);
    }
  };

  /**
   * Reject all cookies via Klaro and save the decision
   */
  // eslint-disable-next-line no-unused-vars
  const resetContentClick = async () => {
    try {
      await KlaroConfig.getManager().changeAll(false);
      onCloseCookieWeight();
      saveContentClick();
    } catch (err) {
      console.error("[Secuvy] Error resetting consent:", err);
    }
  };

  // =====================================================
  // 🔹 Cookie reading
  // =====================================================
  /**
   * Read cookie by name. Special-case: if name === 'secuvy' return true when present.
   * @param {string} name
   * @return {string|boolean|null}
   */
  const getCookieValue = name => {
    try {
      const pattern = new RegExp(`(?:^|; )${name}=([^;]*)`);
      const matches = document.cookie.match(pattern);
      if (name === "secuvy" && matches) return true;
      if (matches) return decodeURIComponent(matches[1]);
      return null;
    } catch (err) {
      console.error("[Secuvy] Error getting cookie value:", err);
      return null;
    }
  };

  // =====================================================
  // 🔹 Cross-window messaging
  // =====================================================
  window.addEventListener("message", event => {
    try {
      if (!dsarDomain || !event.origin) return;
      if (!dsarDomain.includes(event.origin)) return;

      const { data } = event;
      if (!data || !data.type) return;

      switch (data.type) {
        case "iframe-loaded":
          // send config messages after a small delay so iframe ready
          setTimeout(() => {
            const iframe = document.getElementById("secuvy-dsar-frame");

            if (iframe && iframe.contentWindow) {
              try {
                iframe.contentWindow.postMessage({ type: "menu-options", data: menuOptions }, "*");
                iframe.contentWindow.postMessage(
                  { type: "consentId", consentUrl: window.klaroConfig.consentTemplateId },
                  "*",
                );
                iframe.contentWindow.postMessage(
                  {
                    type: "consentUrl",
                    url: consentUrl,
                    consentId: window.klaroConfig.consentTemplateId,
                  },
                  "*",
                );
                setKlaroConfig(true);
              } catch (err) {
                console.error("[Secuvy] Error sending config messages:", err);
              }

              if (isConsentUpdated) {
                try {
                  iframe.contentWindow.postMessage(
                    {
                      type: "route",
                      location: routeLocation,
                      submenu: routeSubmenu,
                      cookiesDetails: KlaroConfig.getManager().savedConsents,
                    },
                    "*",
                  );
                } catch (err) {
                  console.error("[Secuvy] Error sending route message:", err);
                }
              }
            }
          }, 1000);
          break;

        case "secuvy_consent_accept_selected":
          toggleChange(data.cookies);
          break;

        case "api_success": {
          const hdr = document.getElementById("secuvy-iframe-header");
          if (hdr) hdr.style.display = "none";
          break;
        }

        case "client_drawer_close":
          closeSecuvyDsarDrawer();
          break;
      }
    } catch (err) {
      console.error("[Secuvy] Error handling message:", err);
    }
  });

  // =====================================================
  // 🔹 Klaro boot and initialization
  // =====================================================
  window.addEventListener("load", function () {
    const winKlaro = setInterval(() => {
      try {
        if (typeof window.klaro === "object") {
          try {
            const old = document.getElementById("secuvy");
            if (old && old.parentNode) old.parentNode.removeChild(old);
          } catch (err) {
            console.error("[Secuvy] Error removing old widget:", err);
          }

          // set Klaro config details
          // eslint-disable-next-line no-unused-vars
          const { callback, ...others } = window.klaroConfig || {};
          Klaro = others;
          KlaroConfig = window.klaro;

          // cookie cleanup for services
          if (others && Array.isArray(others.services)) cookieDeleteManual(others.services, 10);

          // show/hide widget based on cookie
          const secuvyCard = document.getElementById("secuvy-widget-card");
          const secuvyButton = document.getElementById("secuvy-widget-button");
          const secuvyCustomerExternalLink = document.getElementsByClassName(
            "secuvy_customer_external_link",
          );
          if (secuvyCustomerExternalLink) {
            for (let i = 0; i < secuvyCustomerExternalLink.length; i++) {
              secuvyCustomerExternalLink[i].style.display = "block";
            }
          }

          if (secuvyCard && secuvyButton) {
            if (getCookieValue("secuvy")) {
              secuvyCard.style.display = "none";
              secuvyButton.style.display = "none";
              hideSecuvyMobileWidget();
            } else {
              secuvyCard.style.display = "flex";
              secuvyButton.style.display = "flex";
              hideSecuvyMobileWidget(true);
            }
          }

          // apply translations and styles from Klaro config
          if (others && others.translations && others.translations.en) {
            const t = others.translations.en;
            try {
              const {
                decline,
                ok,
                okButtonColorModal = "#0085ca",
                popupDescription = "This site uses cookies. Some of them are essential, while others help us improve your experience.",
                thatsOkTextColor = "#FFFFFF",
                thatsOkTextSize = "0.8rem",
                declineAllTextColor = "#000000",
                declineAllTextSize = "0.8rem",
                dsrWidgetText = "Do not Sell My Personal Information",
                policiesWidgetText = "View Policies",
                managePerferanceWidgetText = "Manage Preferences",
              } = t;

              const setText = (id, value) => {
                const el = document.getElementById(id);
                if (el) el.innerHTML = value;
              };

              setText("secuvy-weight-reject-all-btn", decline);
              setText("secuvy-weight-accept-all-btn", ok);
              setText("secuvy-widget-link-policies", policiesWidgetText);
              setText("secuvy-widget-link-manage", managePerferanceWidgetText);
              setText("secuvy-widget-link-dsr", dsrWidgetText);

              const setStyle = (id, styleName, value) => {
                const el = document.getElementById(id);
                if (el) el.style[styleName] = value;
              };

              setStyle("secuvy-weight-reject-all-btn", "color", declineAllTextColor);
              setStyle("secuvy-weight-accept-all-btn", "color", thatsOkTextColor);
              setStyle("secuvy-weight-reject-all-btn", "fontSize", declineAllTextSize);
              setStyle("secuvy-weight-accept-all-btn", "fontSize", thatsOkTextSize);
              setStyle("secuvy-weight-accept-all-btn", "backgroundColor", okButtonColorModal);
              setStyle("secuvy-weight-accept-all-btn", "borderColor", okButtonColorModal);

              setText("secuvy-popup-description", popupDescription);
              setText("secuvy-mobile-popup-description", popupDescription);

              setText("secuvy-mobile-weight-reject-all-btn", decline);
              setStyle("secuvy-mobile-weight-reject-all-btn", "color", declineAllTextColor);
              setStyle("secuvy-mobile-weight-reject-all-btn", "fontSize", declineAllTextSize);

              setText("secuvy-mobile-weight-accept-all-btn", ok);
              setStyle("secuvy-mobile-weight-accept-all-btn", "color", thatsOkTextColor);
              setStyle("secuvy-mobile-weight-accept-all-btn", "fontSize", thatsOkTextSize);
              setStyle(
                "secuvy-mobile-weight-accept-all-btn",
                "backgroundColor",
                okButtonColorModal,
              );
              setStyle("secuvy-mobile-weight-accept-all-btn", "borderColor", okButtonColorModal);

              setText("secuvy-mobile-widget-link-policies", policiesWidgetText);
              setText("secuvy-mobile-widget-link-manage", managePerferanceWidgetText);
              setText("secuvy-mobile-widget-link-dsr", dsrWidgetText);

              // GPC: show/hide and color the GPC indicator if enabled in Klaro
              if (Klaro && Klaro.isGpcEnabled) {
                const gpcEl = document.getElementById("secuvy-gpc-section-centent");
                if (gpcEl) gpcEl.style.display = "flex";
                const box = document.getElementById("secuvy-gpc-section-box");
                if (box) {
                  box.style.backgroundColor = navigator.globalPrivacyControl
                    ? "#33bb7b"
                    : "#f05a55";
                }
                const desc = document.getElementById("secuvy-gpc-section-desc");
                if (desc) {
                  desc.innerHTML = `GPC signal ${
                    navigator.globalPrivacyControl ? "detected" : "not detected"
                  }`;
                }

                const mgpc = document.getElementById("secuvy-mobile-gpc-section-centent");
                if (mgpc) mgpc.style.display = "flex";
                const mbox = document.getElementById("secuvy-mobile-gpc-section-box");
                if (mbox) {
                  mbox.style.backgroundColor = navigator.globalPrivacyControl
                    ? "#33bb7b"
                    : "#f05a55";
                }
                const mdesc = document.getElementById("secuvy-mobile-gpc-section-desc");
                if (mdesc) {
                  mdesc.innerHTML = `GPC signal ${
                    navigator.globalPrivacyControl ? "detected" : "not detected"
                  }`;
                }
              }
            } catch (errConfig) {
              console.error("[Secuvy] Error setting Klaro config:", errConfig);
            }
          }

          try {
            openSecuvyDsarDrawer("secuvy-appear-drawer");
          } catch (err) {
            console.error("[Secuvy] consent libs loading inprogress.");
          }

          setTimeout(() => {
            setKlaroConfig(true);
            isConsentUpdated = true;

            // update the secuvy consent status
            updateSecuvyConsent();
            clearInterval(winKlaro);
          }, 90);
        }
      } catch (err) {
        console.error("[Secuvy] Error setting Klaro config:", err);
        // keep polling loop resilient
      }
    }, 100);
  });

  // Close drawer on Escape key
  window.addEventListener("keydown", ev => {
    if (ev.key === "Escape") closeSecuvyDsarDrawer();
  });

  // =====================================================
  // 🔹 Boot: render widget and optionally open drawer
  // =====================================================
  try {
    toRenderWeight();
  } catch (err) {
    console.error("[Secuvy] Error rendering widget:", err);
  }

  // =====================================================
  // 🔹 Expose public functions to global scope
  // =====================================================
  Object.assign(window, {
    dsarDomain,
    showSecuvyWidget,
    hideSecuvyMobileWidget,
    openSecuvyDsarDrawer,
    closeSecuvyDsarDrawer,
    toRouteIframeChild,
    onCloseCookieWeight,
    createVisitorCookie,
    saveContentClick,
    resetContentClick,
  });
})();